
import { ComputedRef, defineComponent, onMounted, ref, Ref } from "vue";
import { computed } from "@vue/reactivity";
import { useStore } from "vuex";
import { dispatchErrorBox, dispatchSuccessBox } from "../../components/mixins";
import router from "../../../../src/routes";
import { Product } from "../../../../domain/entities/shop/product/product";
import { User } from "../../../../domain/entities/entities";
import { RepositoryException } from "../../../../domain/exceptions/exceptions";
import ShopImageGallery from "../../components/application/shop/ShopImageGallery.vue";
import { useRoute } from "vue-router";
import ProductHeader from "./ProductHeader.vue";
import NameRedirect from "./NameRedirect.vue";
import ProductInformation from "./ProductInformation.vue";
import ProductImages from "./ProductImages.vue";
import ProductCarousel from "./ProductCarousel.vue";
import ProductButtons from "./ProductButtons.vue";

export default defineComponent({
  components: {
    ShopImageGallery,
    ProductHeader,
    NameRedirect,
    ProductInformation,
    ProductImages,
    ProductCarousel,
    ProductButtons,
  },
  setup() {
    const currentSlide = ref(0);
    const isModalOpen = ref(false);
    const selectedImage = ref(0);

    function slideTo(val) {
      currentSlide.value = val;
    }

    const store = useStore();
    const route = useRoute();

    /** Get the id of the product that is in the URl.*/
    const productID: ComputedRef<number> = computed(() => {
      return Number(router.currentRoute.value.params.productID);
    });

    /** Variable that returns a product with its information. */
    const product: ComputedRef<Product> = computed(() => {
      return store.getters["shop_module/product"];
    });

    /** List of products in the cart. */
    const bagProducts: ComputedRef<Array<Product>> = computed(() => {
      return store.getters["shop_module/productsInBag"];
    });

    /** Variable to get the user data. */
    const user: ComputedRef<User> = computed(() => {
      return store.getters["userProfile_module/user"];
    });

    /** Getter of the decoins with the subtraction of the values of the products added in the bag. */
    const balance: ComputedRef<number> = computed(() => {
      return store.getters["shop_module/balance"];
    });

    onMounted(async () => {
      try {
        await store.dispatch("shop_module/loadBagProducts");
        await store.dispatch("shop_module/getProduct", productID.value);
        await store.dispatch("userProfile_module/loadUser");
      } catch (e) {
        if (e instanceof RepositoryException) {
          await dispatchErrorBox(e.message);
        } else {
          console.error(e);
          await dispatchErrorBox("Ocorreu um erro inesperado.");
        }
      }
    });

    /** Function to add products to cart */
    async function addToBag(product: Product): Promise<void> {
      try {
        product.quantity = 1;
        if (product.stock <= 0) {
          return;
        } else if (product.decoins > user.value.decoins) {
          return;
        } else if (balance.value < product.decoins) {
          return;
        } else if (product.isActive === false) {
          return;
        } else {
          await store.dispatch("shop_module/addToBag", product);
          await dispatchSuccessBox("Produto adicionado no seu carrinho!");
        }
      } catch (e) {
        if (e instanceof RepositoryException) {
          await dispatchErrorBox(e.message);
        } else {
          console.error(e);
          await dispatchErrorBox("Ocorreu um erro inesperado.");
        }
      }
    }

    /** Remove the product from the cart within the favorites list. */
    async function removeFromBag(product: Product): Promise<void> {
      try {
        await store.dispatch("shop_module/removeFromBag", product);
      } catch (e) {
        if (e instanceof RepositoryException) {
          await dispatchErrorBox(e.message);
        } else {
          console.error(e);
          await dispatchErrorBox("Ocorreu um erro inesperado.");
        }
      }
    }

    /** Computed that contains the products added to favorites.*/
    const favoriteProducts: ComputedRef<Array<Product>> = computed(() => {
      return store.getters["shop_module/productsInFavoriteList"];
    });

    /** Function responsible for checking if the product is in the favorites.
     * @param product parameter that receives an object of type Product as a variable. */
    function isFavorite(product: Product) {
      return favoriteProducts.value.find((item) => item.id === product.id);
    }

    /** Function to check if the product in the favorites is already in the cart. */
    function bagIncludes(product: Product) {
      return bagProducts.value.find((item) => item.id === product.id);
    }

    /** Checks if the product that was added to favorites is in stock.*/
    function isProductOutOfStock(product: Product): boolean {
      return product.stock <= 0;
    }

    /** Function responsible for validating the user's decoins. If the user does not have enough decoins to buy
     * the product, the add button in the bag is blocked.
     * @param product parameter that receives an object of type Product as a variable.
     * @see Product */
    function hasEnoughDecoins(product: Product): boolean {
      return product.decoins > user.value.decoins;
    }

    /** Function responsible for validating the user's decoins along with the stock.
     * Function used to remove the add to bag button.
     * @param product product to validate stock and decoins. */
    function validateStockAndDecoins(product: Product): boolean | undefined {
      if (hasEnoughDecoins(product) && isProductOutOfStock(product)) {
        return true;
      } else if (!hasEnoughDecoins(product) && isProductOutOfStock(product)) {
        return true;
      } else if (hasEnoughDecoins(product) && !isProductOutOfStock(product)) {
        return true;
      }
    }

    /** Function used to control whether for each product added to the bag it is still possible for the user to add
     * the other products that are available
     * @param product product to do validation. */
    function checkEnough(product: Product) {
      const remain = balance.value - product.decoins;
      const inBag = bagIncludes(product);

      if (product.stock <= 0) {
        return true;
      }

      if (inBag) {
        return true;
      }

      if (remain >= 0) {
        return true;
      }

      return false;
    }

    /** Function responsible for displaying a label informing the product status. */
    function setButtonText(product: Product): string {
      if (!checkEnough(product)) {
        return "decoins insuficientes";
      } else if (isProductOutOfStock(product)) {
        return "sem estoque";
      } else {
        return "adicionar";
      }
    }

    /** Function responsible for formatting the decoins to Brazilian currency. */
    function formatDecoins(decoins: number) {
      return Intl.NumberFormat("pt-br").format(decoins);
    }

    /** Function responsible for checking if the product was removed from the shop. */
    function checkProductIsActive(product: Product): boolean {
      return product.isActive === false;
    }

    function openImageModal(imageIndex: number) {
      isModalOpen.value = true;
      selectedImage.value = imageIndex;
    }

    const showImagesCarousel = computed(() => {
      return product.value.imagesUrl.length;
    });

    return {
      product,
      user,
      currentSlide,
      isModalOpen,
      selectedImage,
      showImagesCarousel,
      openImageModal,
      slideTo,
      addToBag,
      bagIncludes,
      removeFromBag,
      isProductOutOfStock,
      hasEnoughDecoins,
      validateStockAndDecoins,
      checkEnough,
      setButtonText,
      formatDecoins,
      checkProductIsActive,
      isFavorite,
      bagProducts,
    };
  },
});
